import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './LobsterExpensePieChart.css';

export const LobsterExpensePieChart = ({ annualResults }) => {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState('average');

  console.log('📊 LobsterExpensePieChart received data:', annualResults);

  useEffect(() => {
    if (!annualResults || annualResults.length === 0 || !chartRef.current) return;

    // Clear any existing chart and tooltip
    if (tooltipRef.current) {
      tooltipRef.current.remove();
    }

    // Create tooltip
    tooltipRef.current = d3.select('body')
      .append("div")
      .attr("class", "chart-tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("z-index", "9999");

    // Clear any existing chart
    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    // Calculate expense data
    let expenseData;
    if (selectedYear === 'average') {
      // Average across all years
      const totals = annualResults.reduce((acc, year) => ({
        bait_cost: acc.bait_cost + (year.bait_cost || 0),
        fuel_cost: acc.fuel_cost + (year.fuel_cost || 0),
        other_opex: acc.other_opex + (year.other_opex || 0),
        gear_depreciation: acc.gear_depreciation + (year.gear_depreciation || 0),
        capex: (acc.capex || 0) + (year.capex || 0)
      }), { bait_cost: 0, fuel_cost: 0, other_opex: 0, gear_depreciation: 0, capex: 0 });

      const count = annualResults.length;
      expenseData = [
        { label: 'Bait Cost', value: totals.bait_cost / count, color: '#3b82f6' },
        { label: 'Fuel Cost', value: totals.fuel_cost / count, color: '#f59e0b' },
        { label: 'Crew & Operating', value: totals.other_opex / count, color: '#10b981' },
        { label: 'Gear Depreciation', value: totals.gear_depreciation / count, color: '#8b5cf6' },
        { label: 'Capital Payment', value: totals.capex / count, color: '#ef4444' }
      ];
    } else {
      // Specific year
      const yearData = annualResults.find(y => y.year === parseInt(selectedYear));
      if (!yearData) return;

      expenseData = [
        { label: 'Bait Cost', value: yearData.bait_cost || 0, color: '#3b82f6' },
        { label: 'Fuel Cost', value: yearData.fuel_cost || 0, color: '#f59e0b' },
        { label: 'Crew & Operating', value: yearData.other_opex || 0, color: '#10b981' },
        { label: 'Gear Depreciation', value: yearData.gear_depreciation || 0, color: '#8b5cf6' },
        { label: 'Capital Payment', value: yearData.capex || 0, color: '#ef4444' }
      ];
    }

    // Calculate total and percentages
    const total = expenseData.reduce((sum, d) => sum + d.value, 0);
    expenseData.forEach(d => {
      d.percentage = (d.value / total) * 100;
    });

    // Set up dimensions
    const width = 400;
    const height = 400;
    const radius = Math.min(width, height) / 2 - 40;

    // Create chart group
    const g = svg
      .attr("width", width + 300) // Extra space for legend
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    // Create pie generator
    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);

    // Create arc generator
    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(radius);

    // Create hover arc (slightly larger)
    const arcHover = d3.arc()
      .innerRadius(0)
      .outerRadius(radius + 10);

    // Draw pie slices
    const slices = g.selectAll('.slice')
      .data(pie(expenseData))
      .enter()
      .append('g')
      .attr('class', 'slice');

    slices.append('path')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arcHover);

        const tooltip = tooltipRef.current;
        tooltip
          .style("opacity", 1)
          .html(`
            <p class="tooltip-title">${d.data.label}</p>
            <p><strong>Amount:</strong> $${d.data.value.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
            <p><strong>Percentage:</strong> ${d.data.percentage.toFixed(1)}%</p>
          `)
          .style("left", `${event.pageX + 10}px`)
          .style("top", `${event.pageY - 28}px`);
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arc);

        tooltipRef.current.style("opacity", 0);
      });

    // Add percentage labels
    slices.append('text')
      .attr('transform', d => {
        const pos = arc.centroid(d);
        return `translate(${pos[0]}, ${pos[1]})`;
      })
      .attr('text-anchor', 'middle')
      .style('fill', 'white')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .style('pointer-events', 'none')
      .text(d => d.data.percentage >= 5 ? `${d.data.percentage.toFixed(1)}%` : '');

    // Create legend
    const legend = svg.append('g')
      .attr('transform', `translate(${width + 20}, 80)`);

    const legendItems = legend.selectAll('.legend-item')
      .data(expenseData)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(0, ${i * 30})`);

    legendItems.append('rect')
      .attr('width', 20)
      .attr('height', 20)
      .attr('fill', d => d.color);

    legendItems.append('text')
      .attr('x', 30)
      .attr('y', 15)
      .style('font-size', '13px')
      .style('fill', '#374151')
      .text(d => `${d.label}: $${(d.value / 1000).toFixed(1)}K`);

    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, [annualResults, selectedYear]);

  if (!annualResults || annualResults.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        No expense data available.
      </div>
    );
  }

  const years = annualResults.map(r => r.year);

  return (
    <div className="lobster-expense-chart-container">
      {/* Year selector */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <label style={{ marginRight: '10px', fontWeight: '500' }}>View expenses for:</label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: '4px',
            border: '1px solid #d1d5db',
            fontSize: '14px'
          }}
        >
          <option value="average">Average (All Years)</option>
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Chart */}
      <div className="plot-content" style={{ display: 'flex', justifyContent: 'center' }}>
        <svg ref={chartRef}></svg>
      </div>
    </div>
  );
};

export default LobsterExpensePieChart;